// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, Step, StepLabel, Stepper, TextField,
} from '@mui/material';
import api from '../../utils/api';
import { SiteFields } from './DialogueSite';
import { errorMessage } from '../../utils/erreurs';
import {
  creationBody, EMPTY_DIRECTOR, PLAUSIBLE_EMAIL, generatePassword, EMPTY_GROUP, EMPTY_SITE,
} from './commun';

/**
 * Ouvre un client établissement en trois écritures : client, établissement,
 * puis compte de direction avec `must_change_password=True`.
 *
 * `POST /api/pro/admin/bootstrap` ne convient pas : mot de passe choisi par
 * Vigeos sans changement obligatoire, aucun contrôle d'homonymie, ni adresse
 * ni fuseau ni serveur, et une option `demo` qui fabrique des consentements
 * RGPD. Chambres, résidents, consentements et personnel se gèrent dans la
 * console du client (gestion.vigeos.fr), fermée au jeton de l'équipe Vigeos.
 */
export default function OpeningWizard({ ouvert: isOpen, comptesVigeos: vigeosAccounts, onFermer: onClose, onRecharger: onReload, onOuvert: onOpened }) {
  const [state, setState] = useState(null);

  // L'état naît à l'ouverture et disparaît à la fermeture, mais pas entre deux
  // essais : ce qui a été créé avant une erreur doit survivre, sans quoi un
  // nouvel essai recréerait le client.
  useEffect(() => {
    if (!isOpen) { setState(null); return; }
    setState((a) => a || {
      etape: 0, groupe: { ...EMPTY_GROUP }, site: { ...EMPTY_SITE },
      direction: { ...EMPTY_DIRECTOR },
      cree: { organisation_id: null, organisation_nom: '', site_id: null, site_nom: '' },
      erreur: '', enCours: false,
    });
  }, [isOpen]);

  if (!isOpen || !state) return null;

  const update = (patch) => setState((a) => (a ? { ...a, ...patch } : a));

  const isStepValid = (a) => {
    if (a.etape === 0) return a.cree.organisation_id !== null || a.groupe.name.trim().length >= 2;
    if (a.etape === 1) return a.cree.site_id !== null || a.site.name.trim().length >= 2;
    return PLAUSIBLE_EMAIL.test(a.direction.email.trim());
  };

  /**
   * Les trois écritures ne partagent pas de transaction : si la deuxième
   * échoue, le client existe déjà. Ce qui est passé est retenu, l'assistant
   * revient sur l'étape fautive avec le message de l'API, et un nouvel essai
   * ne recrée rien.
   */
  const executer = async () => {
    const a = state;
    update({ enCours: true, erreur: '' });

    let organizationId = a.cree.organisation_id;
    let organizationName = a.cree.organisation_nom;
    if (!organizationId) {
      try {
        const body = creationBody({
          ...a.groupe,
          contract_start: a.groupe.contract_start ? `${a.groupe.contract_start}T00:00:00` : '',
        });
        const res = await api.post('/pro/admin/organisations', body);
        organizationId = res.data.id;
        organizationName = res.data.name;
      } catch (e) {
        update({ enCours: false, etape: 0, erreur: `Création du client : ${errorMessage(e, 'échec')}` });
        return;
      }
    }

    let siteId = a.cree.site_id;
    let siteName = a.cree.site_nom;
    if (!siteId) {
      try {
        const res = await api.post(`/pro/admin/organisations/${organizationId}/sites`, creationBody(a.site));
        siteId = res.data.id;
        siteName = res.data.name;
      } catch (e) {
        update({
          enCours: false, etape: 1,
          cree: { organisation_id: organizationId, organisation_nom: organizationName, site_id: null, site_nom: '' },
          erreur: `Rattachement de l'établissement : ${errorMessage(e, 'échec')}`,
        });
        onReload();
        return;
      }
    }

    // Le mot de passe est tiré ici et nulle part ailleurs : s'il n'est pas
    // accepté, la variable meurt avec la fonction et un nouvel essai en tire un
    // autre. Rien n'est journalisé.
    const email = a.direction.email.trim().toLowerCase();
    const password = generatePassword(email);
    try {
      await api.post(`/pro/admin/organisations/${organizationId}/staff`, {
        email,
        full_name: a.direction.full_name.trim() || null,
        password: password,
        // Compte org-wide : la direction voit tous les établissements du
        // client, y compris ceux rattachés plus tard.
        site_id: null,
      });
    } catch (e) {
      update({
        enCours: false, etape: 2,
        cree: { organisation_id: organizationId, organisation_nom: organizationName, site_id: siteId, site_nom: siteName },
        erreur: `Compte de direction : ${errorMessage(e, 'échec')}`,
      });
      // Recharger la liste sans fermer : le client et son établissement
      // existent déjà, et un nouvel essai reprend à l'étape fautive.
      onReload();
      return;
    }

    onOpened({
      organisationId: organizationId,
      secret: { email, motDePasse: password, contexte: `${organizationName} / ${siteName}` },
    });
  };

  return (
    <Dialog open onClose={() => !state.enCours && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Nouveau client établissement</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={state.etape} sx={{ mb: 3 }}>
          <Step completed={!!state.cree.organisation_id}><StepLabel>Client</StepLabel></Step>
          <Step completed={!!state.cree.site_id}><StepLabel>Établissement</StepLabel></Step>
          <Step><StepLabel>Compte de direction</StepLabel></Step>
        </Stepper>

        {state.erreur && <Alert severity="error" sx={{ mb: 2 }}>{state.erreur}</Alert>}

        {state.etape === 0 && (
          <>
            {state.cree.organisation_id ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Le client « {state.cree.organisation_nom} » a été créé. Il ne sera pas
                recréé : corrigez la suite, ou fermez et reprenez depuis sa fiche.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Le client porte le contrat, l&apos;établissement porte les murs. Si ce
                client existe déjà, fermez cet assistant et ouvrez sa fiche : le
                bouton « Rattacher un établissement » suffit, et sa direction verra
                le nouvel établissement sans autre geste.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" required label="Nom du client"
                  value={state.groupe.name}
                  disabled={!!state.cree.organisation_id}
                  onChange={(e) => update({ groupe: { ...state.groupe, name: e.target.value } })}
                  helperText="Nom d'usage, pas la raison sociale. Refusé si un client porte déjà exactement le même nom."
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Contact chez le client"
                  value={state.groupe.contact_name}
                  disabled={!!state.cree.organisation_id}
                  onChange={(e) => update({ groupe: { ...state.groupe, contact_name: e.target.value } })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Courriel du contact"
                  value={state.groupe.contact_email}
                  disabled={!!state.cree.organisation_id}
                  onChange={(e) => update({ groupe: { ...state.groupe, contact_email: e.target.value } })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Téléphone du contact"
                  value={state.groupe.contact_phone}
                  disabled={!!state.cree.organisation_id}
                  onChange={(e) => update({ groupe: { ...state.groupe, contact_phone: e.target.value } })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Début de contrat"
                  InputLabelProps={{ shrink: true }}
                  value={state.groupe.contract_start}
                  disabled={!!state.cree.organisation_id}
                  onChange={(e) => update({ groupe: { ...state.groupe, contract_start: e.target.value } })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notes" multiline rows={2}
                  value={state.groupe.notes}
                  disabled={!!state.cree.organisation_id}
                  onChange={(e) => update({ groupe: { ...state.groupe, notes: e.target.value } })} />
              </Grid>
            </Grid>
          </>
        )}

        {state.etape === 1 && (
          <>
            {state.cree.site_id && (
              <Alert severity="success" sx={{ mb: 2 }}>
                L&apos;établissement « {state.cree.site_nom} » a été rattaché. Il ne sera
                pas recréé.
              </Alert>
            )}
            <SiteFields
              valeurs={state.site}
              onChange={(site) => update({ site })}
              disabled={!!state.cree.site_id}
              comptesVigeos={vigeosAccounts}
            />
          </>
        )}

        {state.etape === 2 && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Ce compte est la porte d&apos;entrée du client : il voit tous ses
              établissements, présents et à venir, et c&apos;est lui qui créera ensuite
              les résidents, leur consentement et le reste du personnel depuis
              gestion.vigeos.fr. Les chambres peuvent aussi s&apos;importer depuis la
              fiche du client, le jour de la pose. Son mot de passe est tiré au hasard par cet écran,
              affiché une seule fois, et devra être changé à la première connexion.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" required type="email"
                  label="Courriel de la direction"
                  value={state.direction.email}
                  onChange={(e) => update({ direction: { ...state.direction, email: e.target.value } })}
                  helperText="Identifiant de connexion. Refusé si un compte pro existe déjà avec cette adresse, chez ce client ou ailleurs." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Nom et prénom"
                  value={state.direction.full_name}
                  onChange={(e) => update({ direction: { ...state.direction, full_name: e.target.value } })} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={state.enCours}>Fermer</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => update({ etape: state.etape - 1, erreur: '' })}
          disabled={state.etape === 0 || state.enCours}
        >
          Retour
        </Button>
        {state.etape < 2 ? (
          <Button variant="contained" onClick={() => update({ etape: state.etape + 1, erreur: '' })}
            disabled={!isStepValid(state)}>
            Suivant
          </Button>
        ) : (
          <Button variant="contained" onClick={executer}
            disabled={!isStepValid(state) || state.enCours}>
            {state.enCours ? <CircularProgress size={18} /> : 'Ouvrir le client'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
